/** Reads a Relay connection to its last page, for screens that must show the whole list. */
import { useEffect, useRef } from 'react';

/** Rows per request; every page is read, so this only sizes one round trip. */
export const MATRIX_PAGE_SIZE = 500;

/** Rows read per connection before giving up, so a list past it is reported, not silently cut. */
export const MATRIX_MAX_ROWS = 5000;

export interface ConnectionPage {
  edges: readonly unknown[];
  pageInfo: { hasNextPage: boolean; endCursor?: string | null };
}

export interface PageState {
  loadedCount: number;
  hasNextPage: boolean;
  endCursor: string | null;
}

type FetchMore = (options: { variables: { after: string } }) => Promise<unknown>;

export function pageStateOf(connection: ConnectionPage | null | undefined): PageState {
  return {
    loadedCount: connection?.edges.length ?? 0,
    hasNextPage: connection?.pageInfo.hasNextPage ?? false,
    endCursor: connection?.pageInfo.endCursor ?? null
  };
}

/** Follows `endCursor` until the connection ends or the cap is hit; the cache policy merges pages. */
export function useReadAllPages(
  page: PageState,
  fetchMore: FetchMore,
  onError: (error: Error) => void
): { loadingMore: boolean; exhausted: boolean } {
  const inFlightCursor = useRef<string | null>(null);
  const wantsMore = page.hasNextPage && page.loadedCount < MATRIX_MAX_ROWS;
  const exhausted = page.hasNextPage && page.loadedCount >= MATRIX_MAX_ROWS;
  const { endCursor } = page;

  useEffect(() => {
    if (!wantsMore || !endCursor || inFlightCursor.current === endCursor) return;
    inFlightCursor.current = endCursor;
    fetchMore({ variables: { after: endCursor } })
      .catch((error: unknown) => onError(error instanceof Error ? error : new Error(String(error))))
      .finally(() => {
        if (inFlightCursor.current === endCursor) inFlightCursor.current = null;
      });
  }, [endCursor, fetchMore, onError, wantsMore]);

  return { loadingMore: wantsMore, exhausted };
}
